import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import Attention from "../components/Attention";
import SubFlowNav, { FocusPanel } from "../components/SubFlow";
import {
  AnalyticsBlockers,
  AnalyticsCharts,
  AnalyticsHealth,
  AnalyticsPipeline,
  AnalyticsStrategy,
} from "../components/AnalyticsOverview";
import PersonaLanding from "../components/PersonaLanding";
import StorySpine from "../components/StorySpine";
import { actionOpenHref } from "../lib/actionLinks";
import { analyticsFlowsFor, collectBlockers, focusHref } from "../lib/analytics";
import { navItemAllowed, showStorySpine } from "../lib/master";
import { PageWait } from "../lib/ui";
import { useSubFlow } from "../lib/subflow";
import { withApp } from "../lib/story";

const DETAIL_LABEL = {
  "/decision": "7R Decision",
  "/delivery": "Value & Delivery",
  "/governance": "Governance",
  "/cockpit": "Decision Cockpit",
  "/lifecycle": "Lifecycle",
  "/program": "Program",
  "/assessment": "Assessment",
  "/evidence": "Evidence",
};

const FOCUS_TITLE = {
  actions: "Queue and blockers",
  insights: "Pipeline, health and 7R",
};

export default function Portfolio({ portfolio, onStartTour, user }) {
  const navigate = useNavigate();
  const { focus } = useSubFlow("overview");
  const [buFilter, setBuFilter] = useState("");
  if (!portfolio) return <PageWait />;

  const { applications } = portfolio;
  const businessUnits = portfolio.business_units || [];
  const visibleApps = buFilter
    ? applications.filter((a) => (a.portfolio_unit || "") === buFilter)
    : applications;
  const filteredPortfolio = { ...portfolio, applications: visibleApps };
  const guide = user?.guide || portfolio.master?.guide || {};
  const count = visibleApps.length;
  const flows = analyticsFlowsFor(guide);
  const queueRows = collectBlockers(portfolio);
  const firstQueueApp = queueRows[0]?.app || visibleApps[0]?.id;
  const canOpenApps = guide.can_drill !== false && navItemAllowed("/applications", guide);
  const detailRoute = guide.detail_route || "/";
  const detailLabel = DETAIL_LABEL[detailRoute] || "Your workspace";
  const showDetailCta = detailRoute !== "/" && navItemAllowed(detailRoute, guide);
  const isOverview = focus === "overview";

  const footerActions = (
    <div className="analytics-footer-actions">
      {showDetailCta && (
        <button
          data-testid="open-role-workspace"
          className="secondary-button"
          type="button"
          onClick={() => {
            const firstItem = queueRows[0]?.item || queueRows[0];
            navigate(firstItem ? actionOpenHref(firstItem, guide, firstQueueApp) : withApp(detailRoute, firstQueueApp));
          }}
        >
          {detailLabel} <ChevronRight size={14} />
        </button>
      )}
      {canOpenApps && (
        <Link data-testid="open-applications" className="text-button" to="/applications">
          All projects ({count}) <ArrowUpRight size={14} />
        </Link>
      )}
      {onStartTour && navItemAllowed("/cockpit", guide) && (
        <button data-testid="portfolio-start-tour" className="text-button" type="button" onClick={onStartTour}>
          <PlayCircle size={14} /> Tour
        </button>
      )}
    </div>
  );

  return (
    <div className="dashboard analytics-dashboard subflow-page" data-testid="portfolio-analytics">
      {isOverview ? (
        <>
          <PersonaLanding portfolio={filteredPortfolio} guide={guide} user={user} onStartTour={onStartTour} />
            {businessUnits.length > 0 && (
            <div className="portfolio-bu-filter" data-testid="portfolio-bu-filter">
              <button type="button" className={!buFilter ? "primary-button small-btn" : "secondary-button small-btn"} onClick={() => setBuFilter("")}>
                All BUs
              </button>
              {businessUnits.map((bu) => (
                <button
                  key={bu.id}
                  type="button"
                  className={buFilter === bu.id ? "primary-button small-btn" : "secondary-button small-btn"}
                  data-testid={`portfolio-bu-${bu.id}`}
                  onClick={() => setBuFilter(bu.id)}
                >
                  {bu.name} ({bu.application_count ?? "—"})
                </button>
              ))}
            </div>
          )}
          {footerActions}
          {!canOpenApps && (
            <p className="muted small-note" data-testid="portfolio-depth-cue">
              Technical detail stays with {guide.accountable_owner || "the Product Owner"}.
            </p>
          )}
        </>
      ) : (
        <>
          <Link to={focusHref("overview")} className="analytics-back" data-testid="analytics-back">
            <ChevronLeft size={16} /> Back to report
          </Link>
          <FocusPanel
            kicker={`${guide.kicker || "REPORT"} · ${guide.role}`}
            title={FOCUS_TITLE[focus] || focus}
            detail={guide.context}
            testId="analytics-head"
          />
          <SubFlowNav items={flows} focus={focus} testId="analytics-subflow" />
          {showStorySpine(guide) && <StorySpine current="portfolio" compact />}

          {focus === "actions" && (
            <>
              <Attention
                items={portfolio.attention}
                approvals={portfolio.approvals}
                actions={portfolio.actions}
                waiting={portfolio.waiting}
                guide={guide}
              />
              <AnalyticsBlockers portfolio={portfolio} guide={guide} />
            </>
          )}
          {focus === "insights" && (
            <>
              <AnalyticsPipeline portfolio={portfolio} guide={guide} />
              <AnalyticsHealth portfolio={portfolio} guide={guide} />
              <AnalyticsStrategy portfolio={portfolio} guide={guide} />
            </>
          )}
          {footerActions}
        </>
      )}
    </div>
  );
}
